import { useState, useRef, useCallback, useEffect } from 'react'
import { WEBSOCKET_ENDPOINTS } from '../contracts/endpoints'
import type {
  GeminiLiveLogEntry,
  GeminiLiveWebSocketMessage,
  GeminiLiveWebSocketResponse,
} from '../types'
import { API_BASE_URL } from '../services/api'
import { resolveWebSocketUrl } from '../services/urls'
import {
  beginIntentionalClose,
  clearSocketReference,
  getPreOpenRejectionMessage,
  markWebSocketOpened,
  prepareWebSocketConnect,
  registerSlotReleaseOnPageHide,
  startKeepAlive,
  stopKeepAlive,
} from './websocketLifecycle'
import {
  GEMINI_INPUT_SAMPLE_RATE,
  GEMINI_UPLOAD_CHUNK_MS,
  GeminiPcmPlaybackScheduler,
  normalizeGeminiAudioFormat,
  resampleFloatToPCM16,
  rmsFromFloat32,
  rmsFromPCM16,
  smoothLevel,
} from '../utils/geminiLiveAudio'

interface GeminiLiveSessionConfig {
  voice?: string
}

interface GeminiLiveSessionState {
  isConnected: boolean
  isSessionActive: boolean
  error: string | null
  status: 'idle' | 'connecting' | 'connected' | 'active' | 'failed'
}

const getCloseError = (code: number): string | null => {
  if (code === 4401) return 'Authentication required. Please sign in again.'
  if (code === 4429) return 'Gemini Live hit a realtime connection limit. Please wait a moment and retry.'
  if (code === 4408) return 'Audio frame too large for realtime processing.'
  // 1006 is an abnormal close with no close frame: the connection was severed
  // rather than shut down. In practice that is the server going away mid-call
  // -- a deploy, a restart, or a dropped network. It used to map to null,
  // which left the session looking like the user had simply stopped it.
  if (code === 1006) {
    return 'The voice connection dropped unexpectedly. This usually means the server restarted. Start a new session to continue.'
  }
  if (code === 1001) return 'The voice service went away. Start a new session to continue.'
  if (code === 1011) return 'The voice service hit an internal error. Start a new session to continue.'
  return 'The voice session ended unexpectedly. Start a new session to continue.'
}

const normalizeIncomingMessage = (raw: unknown): GeminiLiveWebSocketResponse | null => {
  if (!raw || typeof raw !== 'object') {
    return null
  }

  const payload = raw as Record<string, unknown>
  if (typeof payload.type === 'string') {
    return payload as GeminiLiveWebSocketResponse
  }

  if (
    typeof payload.error === 'string'
    || typeof payload.message === 'string'
    || typeof payload.code === 'string'
  ) {
    return {
      type: 'error',
      message: typeof payload.message === 'string' ? payload.message : (
        typeof payload.error === 'string' ? payload.error : 'Unknown error'
      ),
      error: typeof payload.error === 'string' ? payload.error : undefined,
      code: typeof payload.code === 'string' ? payload.code : undefined,
    }
  }

  return null
}

// AudioWorklet processor code
const audioWorkletCode = `
class PCMRecorderProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const configuredFrames = options?.processorOptions?.chunkFrames;
    this.chunkFrames = Number.isFinite(configuredFrames) && configuredFrames > 0
      ? configuredFrames
      : 2048;
    this.buffer = new Float32Array(this.chunkFrames);
    this.bufferIndex = 0;
  }

  process(inputs) {
    const input = inputs[0];
    if (input.length > 0) {
      const channelData = input[0];
      for (let i = 0; i < channelData.length; i++) {
        this.buffer[this.bufferIndex++] = channelData[i];
        if (this.bufferIndex >= this.chunkFrames) {
          this.port.postMessage(this.buffer.buffer, [this.buffer.buffer]);
          this.buffer = new Float32Array(this.chunkFrames);
          this.bufferIndex = 0;
        }
      }
    }
    return true;
  }
}

registerProcessor('pcm-recorder-processor', PCMRecorderProcessor);
`

/**
 * Hook for managing Gemini Live WebSocket audio sessions.
 * Provides bidirectional PCM audio streaming with Gemini's Live API.
 *
 * @param config - Session configuration options
 * @returns Object with session state and control functions
 */
export const useGeminiLiveSession = (config: GeminiLiveSessionConfig = {}) => {
  const [state, setState] = useState<GeminiLiveSessionState>({
    isConnected: false,
    isSessionActive: false,
    error: null,
    status: 'idle'
  })

  const [logs, setLogs] = useState<GeminiLiveLogEntry[]>([])

  const socketRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<number | null>(null)
  const reconnectAttemptRef = useRef(0)
  const intentionalCloseRef = useRef(false)
  const isMountedRef = useRef(false)
  const keepAliveIntervalRef = useRef<number | null>(null)
  const inputAudioContextRef = useRef<AudioContext | null>(null)
  const outputAudioContextRef = useRef<AudioContext | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const workletNodeRef = useRef<AudioWorkletNode | null>(null)
  const playbackSchedulerRef = useRef<GeminiPcmPlaybackScheduler | null>(null)
  const socketClosePromiseRef = useRef<Promise<void> | null>(null)
  const socketCloseResolveRef = useRef<(() => void) | null>(null)
  const closingSocketRef = useRef<WebSocket | null>(null)
  const lastServerErrorMessageRef = useRef<string | null>(null)
  const audioFormatRef = useRef(normalizeGeminiAudioFormat())
  const isSessionActiveRef = useRef<boolean>(false)
  const sessionStartedAtRef = useRef<number | null>(null)
  const firstAudioLoggedRef = useRef<boolean>(false)
  const statsLoggedRef = useRef<boolean>(false)
  const inputSampleRateRef = useRef<number | null>(null)
  const upstreamBytesRef = useRef<number>(0)
  const upstreamChunksRef = useRef<number>(0)
  const downstreamBytesRef = useRef<number>(0)
  const downstreamChunksRef = useRef<number>(0)
  const outputUnderrunsRef = useRef<number>(0)

  // Live audio levels, 0..1. Held in refs rather than state on purpose: these
  // update at audio-chunk rate (~50/s), and routing that through setState would
  // re-render the page on every chunk. The UI reads them from a rAF loop and
  // writes a CSS custom property, so the meter animates without React involved.
  const inputLevelRef = useRef<number>(0)
  const outputLevelRef = useRef<number>(0)

  /**
   * Add a log entry to the session log.
   */
  const addLog = useCallback((tag: GeminiLiveLogEntry['tag'], message: string) => {
    if (!isMountedRef.current) {
      return
    }

    setLogs(prev => [...prev, {
      timestamp: new Date().toISOString(),
      tag,
      message
    }])
  }, [])

  const resetAudioDiagnostics = useCallback(() => {
    audioFormatRef.current = normalizeGeminiAudioFormat()
    lastServerErrorMessageRef.current = null
    sessionStartedAtRef.current = null
    firstAudioLoggedRef.current = false
    statsLoggedRef.current = false
    inputSampleRateRef.current = null
    upstreamBytesRef.current = 0
    upstreamChunksRef.current = 0
    downstreamBytesRef.current = 0
    downstreamChunksRef.current = 0
    outputUnderrunsRef.current = 0
  }, [])

  const logAudioDiagnostics = useCallback((reason: string) => {
    if (statsLoggedRef.current) {
      return
    }

    statsLoggedRef.current = true
    console.info('Gemini Live audio diagnostics', {
      reason,
      inputSampleRate: inputSampleRateRef.current,
      outputSampleRate: audioFormatRef.current.sampleRate,
      upstreamBytes: upstreamBytesRef.current,
      upstreamChunks: upstreamChunksRef.current,
      downstreamBytes: downstreamBytesRef.current,
      downstreamChunks: downstreamChunksRef.current,
      outputUnderruns: outputUnderrunsRef.current,
    })
  }, [])

  const resolveSocketCloseWaiter = useCallback((socket?: WebSocket | null) => {
    if (socket && closingSocketRef.current && socket !== closingSocketRef.current) {
      return
    }

    socketCloseResolveRef.current?.()
    socketCloseResolveRef.current = null
    socketClosePromiseRef.current = null
    closingSocketRef.current = null
  }, [])

  const waitForSocketClose = useCallback((socket: WebSocket | null): Promise<void> => {
    if (!socket || socket.readyState === WebSocket.CLOSED) {
      return Promise.resolve()
    }

    if (socketClosePromiseRef.current && closingSocketRef.current === socket) {
      return socketClosePromiseRef.current
    }

    closingSocketRef.current = socket
    socketClosePromiseRef.current = new Promise<void>((resolve) => {
      const timeoutId = window.setTimeout(() => {
        resolveSocketCloseWaiter(socket)
      }, 1000)

      socketCloseResolveRef.current = () => {
        window.clearTimeout(timeoutId)
        resolve()
      }
    })

    return socketClosePromiseRef.current
  }, [resolveSocketCloseWaiter])

  const closeSocketGracefully = useCallback(async (socket: WebSocket | null) => {
    if (!socket) {
      resolveSocketCloseWaiter(socket)
      return
    }

    const closePromise = waitForSocketClose(socket)

    try {
      socket.close()
    } catch (error) {
      resolveSocketCloseWaiter(socket)
      throw error
    }

    if (socket.readyState === WebSocket.CLOSED) {
      resolveSocketCloseWaiter(socket)
    }

    await closePromise
  }, [resolveSocketCloseWaiter, waitForSocketClose])

  const teardownAudioResources = useCallback(async () => {
    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect()
      workletNodeRef.current = null
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop())
      mediaStreamRef.current = null
    }

    if (inputAudioContextRef.current) {
      if (inputAudioContextRef.current.state !== 'closed') {
        await inputAudioContextRef.current.close()
      }
      inputAudioContextRef.current = null
    }

    if (playbackSchedulerRef.current) {
      playbackSchedulerRef.current.reset()
      playbackSchedulerRef.current = null
    }

    if (outputAudioContextRef.current) {
      if (outputAudioContextRef.current.state !== 'closed') {
        await outputAudioContextRef.current.close()
      }
      outputAudioContextRef.current = null
    }
  }, [])

  const ensureOutputAudioContext = useCallback(async (): Promise<AudioContext> => {
    let outputContext = outputAudioContextRef.current
    if (!outputContext || outputContext.state === 'closed') {
      outputContext = new AudioContext({ latencyHint: 'interactive' })
      outputAudioContextRef.current = outputContext
      playbackSchedulerRef.current = new GeminiPcmPlaybackScheduler(outputContext, audioFormatRef.current)
    } else if (!playbackSchedulerRef.current) {
      playbackSchedulerRef.current = new GeminiPcmPlaybackScheduler(outputContext, audioFormatRef.current)
    }

    if (outputContext.state === 'suspended') {
      await outputContext.resume()
    }

    return outputContext
  }, [])

  const handleIncomingAudioChunk = useCallback(async (buffer: ArrayBuffer) => {
    downstreamBytesRef.current += buffer.byteLength
    downstreamChunksRef.current += 1
    outputLevelRef.current = smoothLevel(outputLevelRef.current, rmsFromPCM16(buffer))

    await ensureOutputAudioContext()
    playbackSchedulerRef.current?.setFormat(audioFormatRef.current)
    const scheduleResult = playbackSchedulerRef.current?.enqueue(buffer)
    outputUnderrunsRef.current = scheduleResult?.underrunCount ?? outputUnderrunsRef.current

    if (scheduleResult?.playbackStarted && !firstAudioLoggedRef.current) {
      firstAudioLoggedRef.current = true
      const startedAt = sessionStartedAtRef.current
      if (startedAt !== null) {
        const firstAudioMs = Math.round(performance.now() - startedAt)
        addLog('info', `First audio in ${firstAudioMs}ms`)
      }
    }
  }, [addLog, ensureOutputAudioContext])

  /**
   * Initialize audio capture with AudioWorklet.
   */
  const initAudio = useCallback(async (): Promise<boolean> => {
    try {
      inputAudioContextRef.current = new AudioContext({ latencyHint: 'interactive' })
      const actualInputSampleRate = inputAudioContextRef.current.sampleRate
      inputSampleRateRef.current = actualInputSampleRate

      mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      })

      const source = inputAudioContextRef.current.createMediaStreamSource(mediaStreamRef.current)

      // Load AudioWorklet
      const blob = new Blob([audioWorkletCode], { type: 'application/javascript' })
      const workletUrl = URL.createObjectURL(blob)
      await inputAudioContextRef.current.audioWorklet.addModule(workletUrl)
      URL.revokeObjectURL(workletUrl)

      const chunkFrames = Math.max(
        128,
        Math.round((actualInputSampleRate * GEMINI_UPLOAD_CHUNK_MS) / 1000)
      )

      workletNodeRef.current = new AudioWorkletNode(
        inputAudioContextRef.current,
        'pcm-recorder-processor',
        {
          processorOptions: {
            chunkFrames,
          },
        }
      )

      workletNodeRef.current.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
        if (socketRef.current?.readyState !== WebSocket.OPEN || !isSessionActiveRef.current) {
          return
        }

        const float32Chunk = new Float32Array(event.data)
        inputLevelRef.current = smoothLevel(inputLevelRef.current, rmsFromFloat32(float32Chunk))
        const pcmChunk = resampleFloatToPCM16(
          float32Chunk,
          inputSampleRateRef.current ?? actualInputSampleRate,
          GEMINI_INPUT_SAMPLE_RATE
        )

        if (!pcmChunk.byteLength) {
          return
        }

        upstreamBytesRef.current += pcmChunk.byteLength
        upstreamChunksRef.current += 1
        socketRef.current.send(pcmChunk.buffer)
      }

      source.connect(workletNodeRef.current)

      if (inputAudioContextRef.current.state === 'suspended') {
        await inputAudioContextRef.current.resume()
      }

      addLog(
        'info',
        `Audio initialized (${Math.round(actualInputSampleRate)}Hz input -> ${GEMINI_INPUT_SAMPLE_RATE}Hz upload)`
      )
      return true

    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error'
      addLog('error', `Failed to initialize audio: ${msg}`)
      console.error('Audio init error:', error)
      return false
    }
  }, [addLog])

  /**
   * Handle incoming WebSocket messages.
   */
  const handleMessage = useCallback((data: GeminiLiveWebSocketResponse) => {
    switch (data.type) {
      case 'connection_ready':
        addLog('info', data.message || 'Connection ready')
        break

      case 'audio_format': {
        const normalizedFormat = normalizeGeminiAudioFormat({
          mimeType: data.mimeType,
          sampleRate: data.sampleRate,
          channels: data.channels,
        })
        audioFormatRef.current = normalizedFormat
        playbackSchedulerRef.current?.setFormat(normalizedFormat)
        addLog('info', `Output audio format ${normalizedFormat.sampleRate}Hz`)
        break
      }

      case 'session_started':
        isSessionActiveRef.current = true
        lastServerErrorMessageRef.current = null
        sessionStartedAtRef.current = performance.now()
        setState(prev => ({ ...prev, isSessionActive: true, status: 'active', error: null }))
        addLog('info', 'Gemini Live session active - speak now!')
        break

      case 'transcript':
        if (data.text) {
          addLog(data.role || 'assistant', data.text)
        }
        break

      case 'turn_complete':
        addLog('info', 'Turn complete')
        break

      case 'session_ended':
        isSessionActiveRef.current = false
        playbackSchedulerRef.current?.reset()
        addLog('info', data.message || 'Session ended')
        setState(prev => ({ ...prev, isSessionActive: false, status: 'connected' }))
        break

      case 'error':
        lastServerErrorMessageRef.current = data.message || data.error || 'Error occurred'
        addLog('error', lastServerErrorMessageRef.current)
        setState(prev => ({ ...prev, error: lastServerErrorMessageRef.current }))
        break

      case 'tool_status': {
        const toolName = data.tool || 'tool'
        const status = data.status || 'executed'
        const tag = status === 'error' ? 'error' : 'info'
        const label =
          status === 'pending_confirmation'
            ? `Waiting for your confirmation to run ${toolName}`
            : status === 'error'
              ? `Tool ${toolName} failed${data.code ? ` (${data.code})` : ''}`
              : `Ran ${toolName}`
        addLog(tag, label)
        break
      }

      default:
        console.warn('useGeminiLiveSession: unknown message type', { type: data.type })
    }
  }, [addLog])

  /**
   * Start a new Gemini Live session.
   */
  const startSession = useCallback(async () => {
    try {
      if (!prepareWebSocketConnect({
        socketRef,
        reconnectTimeoutRef,
        reconnectAttemptRef,
        intentionalCloseRef,
        isMountedRef,
      })) {
        addLog('info', 'Start ignored: Gemini Live connection is already active or closing')
        return
      }

      await teardownAudioResources()
      resetAudioDiagnostics()
      isSessionActiveRef.current = false
      await ensureOutputAudioContext()
      setState(prev => ({ ...prev, status: 'connecting', error: null }))
      addLog('info', 'Starting Gemini Live session...')

      // Initialize audio
      const audioReady = await initAudio()
      if (!audioReady) {
        throw new Error('Failed to initialize audio')
      }

      // Build WebSocket URL
      const wsUrl = resolveWebSocketUrl(WEBSOCKET_ENDPOINTS.geminiLive, {
        baseUrl: API_BASE_URL,
        overrideUrl: import.meta.env.VITE_GEMINI_LIVE_WS_URL,
      })

      const socket = new WebSocket(wsUrl)
      let socketOpened = false
      socket.binaryType = 'arraybuffer'
      socketRef.current = socket

      socket.onopen = () => {
        if (socketRef.current !== socket) {
          return
        }

        socketOpened = true
        markWebSocketOpened(reconnectAttemptRef, reconnectTimeoutRef)
        stopKeepAlive(keepAliveIntervalRef)
        keepAliveIntervalRef.current = startKeepAlive(socketRef)
        addLog('info', 'WebSocket connected')
        setState(prev => ({ ...prev, isConnected: true, status: 'connected' }))

        const startMessage: GeminiLiveWebSocketMessage = {
          type: 'start_session',
          voice: config.voice || 'Puck'
        }

        socket.send(JSON.stringify(startMessage))
      }

      socket.onmessage = async (event) => {
        if (socketRef.current !== socket) {
          return
        }

        if (event.data instanceof ArrayBuffer) {
          await handleIncomingAudioChunk(event.data)
          return
        }

        if (event.data instanceof Blob) {
          const buffer = await event.data.arrayBuffer()
          await handleIncomingAudioChunk(buffer)
          return
        }

        try {
          const raw = JSON.parse(event.data)
          if (raw?.type === 'pong') {
            return
          }
          const data = normalizeIncomingMessage(raw)
          if (!data) {
            console.warn('Unknown Gemini Live message payload:', event.data)
            return
          }
          handleMessage(data)
        } catch (error) {
          console.error('Failed to parse message:', error)
        }
      }

      socket.onclose = (event) => {
        const wasIntentional = intentionalCloseRef.current
        stopKeepAlive(keepAliveIntervalRef)
        clearSocketReference(socketRef, socket)
        resolveSocketCloseWaiter(socket)
        isSessionActiveRef.current = false
        playbackSchedulerRef.current?.reset()
        logAudioDiagnostics('socket_closed')
        const reason = event.reason ? `: ${event.reason}` : ''
        addLog('info', `WebSocket closed (${event.code || 'no-code'}${reason})`)

        if (isMountedRef.current) {
          let nextError = lastServerErrorMessageRef.current
          if (!nextError && !wasIntentional) {
            nextError = socketOpened
              ? getCloseError(event.code)
              : (getCloseError(event.code) ?? getPreOpenRejectionMessage('Gemini live connection'))
          }

          setState(prev => ({
            ...prev,
            isConnected: false,
            isSessionActive: false,
            status: wasIntentional ? 'idle' : 'failed',
            error: wasIntentional ? null : (nextError ?? prev.error),
          }))
        }

        void teardownAudioResources()
      }

      socket.onerror = (error) => {
        if (socketRef.current !== socket) {
          return
        }

        addLog('error', 'WebSocket error')
        console.error('WebSocket error:', error)
      }

    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error)
      addLog('error', msg)
      logAudioDiagnostics('start_failure')
      if (socketRef.current) {
        beginIntentionalClose(intentionalCloseRef, reconnectTimeoutRef)
        await closeSocketGracefully(socketRef.current)
      }
      await teardownAudioResources()
      if (isMountedRef.current) {
        setState(prev => ({ ...prev, status: 'failed', error: msg }))
      }
    }
  }, [
    config.voice,
    addLog,
    closeSocketGracefully,
    ensureOutputAudioContext,
    handleIncomingAudioChunk,
    handleMessage,
    initAudio,
    logAudioDiagnostics,
    resetAudioDiagnostics,
    resolveSocketCloseWaiter,
    teardownAudioResources,
  ])

  /**
   * Stop the current session and clean up resources.
   */
  const stopSession = useCallback(async () => {
    beginIntentionalClose(intentionalCloseRef, reconnectTimeoutRef)
    stopKeepAlive(keepAliveIntervalRef)
    isSessionActiveRef.current = false
    playbackSchedulerRef.current?.reset()
    lastServerErrorMessageRef.current = null

    // Send end session message
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({ type: 'end_session' }))
    }

    logAudioDiagnostics('user_stop')

    // Close WebSocket
    if (socketRef.current) {
      await closeSocketGracefully(socketRef.current)
    }

    await teardownAudioResources()

    if (isMountedRef.current) {
      setState({
        isConnected: false,
        isSessionActive: false,
        error: null,
        status: 'idle'
      })
    }

    inputLevelRef.current = 0
    outputLevelRef.current = 0
    addLog('info', 'Session stopped')
  }, [addLog, closeSocketGracefully, logAudioDiagnostics, teardownAudioResources])

  /**
   * Send a text message to Gemini during an active session.
   */
  const sendTextMessage = useCallback((text: string) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      addLog('error', 'Cannot send message: not connected')
      return
    }

    socketRef.current.send(JSON.stringify({
      type: 'send_text',
      text
    }))

    addLog('user', text)
  }, [addLog])

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true
    const unregisterPageHide = registerSlotReleaseOnPageHide(socketRef)

    return () => {
      isMountedRef.current = false
      unregisterPageHide()
      void stopSession()
    }
  }, [stopSession])

  return {
    ...state,
    logs,
    inputLevelRef,
    outputLevelRef,
    startSession,
    stopSession,
    sendTextMessage
  }
}
